import React, { useState } from 'react';
import { Alert, Button, Input } from '@heroui/react';
import { MailCheck } from 'lucide-react';
import RecoveryApi from './RecoveryApi.js';
import parseRecoveryError from './parseRecoveryError.js';
import AuthCard, { AuthLink } from '../auth-shared/AuthCard.js';
import './recovery-v2.css';

/**
 * RecoveryPage — `/authorization/recovery/`, and the emailed link's target
 * `/authorization/recovery/{token}/`.
 *
 * One page, two states, chosen by whether the URL carries a token: ask for the
 * address, or set the new password.
 *
 * The request step answers the same way for every address, registered or not.
 * That is deliberate on the server and must stay deliberate here — a page that
 * said "check your inbox" only for real accounts would be an address checker.
 */
const RecoveryPage = ({ token = '' }) => {
	const isReset = token !== '';

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [repassword, setRepassword] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [done, setDone] = useState(false);

	const submitRequest = async event => {
		event.preventDefault();
		setError('');
		setLoading(true);
		try {
			await RecoveryApi.requestReset(email);
			setDone(true);
		} catch (caught) {
			setError(parseRecoveryError(caught));
		} finally {
			setLoading(false);
		}
	};

	const submitReset = async event => {
		event.preventDefault();
		setError('');

		if (password !== repassword) {
			setError('Passwords do not match.');
			return;
		}

		setLoading(true);
		try {
			await RecoveryApi.resetPassword({ token, password, passwordConfirmation: repassword });
			setDone(true);
		} catch (caught) {
			setError(parseRecoveryError(caught));
		} finally {
			setLoading(false);
		}
	};

	const control = {
		labelPlacement: 'outside-top',
		variant: 'bordered',
		radius: 'sm',
		classNames: { inputWrapper: 'h-11' }
	};

	// ── Set a new password ──────────────────────────────────────────────────
	if (isReset) {
		if (done) {
			return (
				<div className="auth-v2">
					<AuthCard title="Password updated">
						<div className="flex flex-col items-center gap-4 py-2 text-center">
							<p className="m-0 text-sm text-default-500">
								Your password has been changed. You can sign in with it now.
							</p>
							<Button
								as="a"
								href="/authorization/"
								color="primary"
								radius="sm"
								className="h-11 w-full font-medium"
							>
								Continue to sign in
							</Button>
						</div>
					</AuthCard>
				</div>
			);
		}

		return (
			<div className="auth-v2">
				<AuthCard
					title="Set a new password"
					subtitle="Choose something you have not used here before."
				>
					<form onSubmit={submitReset} className="flex flex-col gap-5">
						{error && (
							<Alert color="danger" variant="flat" radius="sm">
								{error}
							</Alert>
						)}

						<div className="flex flex-col gap-4">
							<Input
								{...control}
								name="password"
								type="password"
								label="New password"
								autoComplete="new-password"
								maxLength={50}
								isRequired
								value={password}
								onValueChange={setPassword}
								description="At least 6 characters."
							/>
							<Input
								{...control}
								name="repassword"
								type="password"
								label="Confirm new password"
								autoComplete="new-password"
								maxLength={50}
								isRequired
								value={repassword}
								onValueChange={setRepassword}
							/>
						</div>

						<Button
							type="submit"
							color="primary"
							radius="sm"
							className="h-11 font-medium"
							isLoading={loading}
						>
							{loading ? 'Saving…' : 'Save password'}
						</Button>
					</form>
				</AuthCard>
			</div>
		);
	}

	// ── Ask for the address ─────────────────────────────────────────────────
	if (done) {
		return (
			<div className="auth-v2">
				<AuthCard
					title="Check your email"
					footer={
						<p className="m-0">
							<AuthLink href="/authorization/">Back to sign in</AuthLink>
						</p>
					}
				>
					<div className="flex flex-col items-center gap-4 py-2 text-center">
						<span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
							<MailCheck className="h-6 w-6 text-success-600" aria-hidden="true" />
						</span>
						<p className="m-0 text-sm text-default-500">
							If an account exists for that address, a reset link is on its way. The link expires,
							so use it soon.
						</p>
					</div>
				</AuthCard>
			</div>
		);
	}

	return (
		<div className="auth-v2">
			<AuthCard
				title="Reset your password"
				subtitle="Enter your email and we will send you a link to set a new one."
				footer={
					<p className="m-0">
						Remembered it? <AuthLink href="/authorization/">Sign in</AuthLink>
					</p>
				}
			>
				<form onSubmit={submitRequest} className="flex flex-col gap-5">
					{error && (
						<Alert color="danger" variant="flat" radius="sm">
							{error}
						</Alert>
					)}

					<Input
						{...control}
						name="email"
						type="email"
						label="Email"
						autoComplete="username"
						maxLength={50}
						isRequired
						value={email}
						onValueChange={setEmail}
					/>

					<Button
						type="submit"
						color="primary"
						radius="sm"
						className="h-11 font-medium"
						isLoading={loading}
					>
						{loading ? 'Sending…' : 'Send reset link'}
					</Button>
				</form>
			</AuthCard>
		</div>
	);
};

export default RecoveryPage;
